/**
 * AccountSummaryBar Component
 *
 * Displays a collapsible account summary bar with key financial metrics.
 * Primary row shows essential info, expanded view shows additional details.
 */

import { ChevronDown } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { IbkrAccountSummary } from "../../types";

export interface AccountSummaryBarProps {
  accountSummary: IbkrAccountSummary | null;
  positionCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isDarkMode: boolean;
  accountError?: string | null;
  isCustomer?: boolean;
  /** When true, show "Connect your IBKR account" overlay without waiting for an API error */
  hasIbkrAccount?: boolean;
  appearance?: "default" | "alpha";
}

/**
 * Format a numeric value as currency
 */
function formatUsdValue(value: number | null | undefined): string {
  const num = Number(value) || 0;
  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const AccountSummaryBar: React.FC<AccountSummaryBarProps> = ({
  accountSummary,
  positionCount,
  isExpanded,
  onToggleExpand,
  isDarkMode,
  accountError,
  isCustomer,
  hasIbkrAccount,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const labelClass = isAlpha
    ? "text-v7-ink/60"
    : isDarkMode ? "text-gray-400" : "text-gray-600";
  const valueClass = isAlpha
    ? "text-v7-ink"
    : isDarkMode ? "text-white" : "text-gray-900";
  const positiveValueClass = isAlpha ? "text-v7-success" : "text-green-500";
  const cautionValueClass = isAlpha ? "text-v7-bronze-dark" : "text-yellow-400";
  const infoValueClass = isAlpha ? "text-v7-ink-soft" : "text-blue-400";
  const chevronClass = isAlpha
    ? "text-v7-ink/50"
    : isDarkMode ? "text-gray-400" : "text-gray-500";
  /**
   * Get ibkrPayload value helper
   */
  const getPayloadValue = (key: string): string | null => {
    if (!accountSummary?.ibkrPayload?.[key]) return null;
    return accountSummary.ibkrPayload[key].value;
  };

  const showConnectPrompt =
    hasIbkrAccount === false ||
    (!isCustomer &&
      accountError &&
      accountError.toLowerCase().includes("no ibkr account configured"));

  return (
    // `ph-mask` redacts all text inside this subtree in PostHog session
    // replays so account balances, buying power, margin requirements, and
    // every other sensitive numeric are never captured.
    <div
      className={`ph-mask relative overflow-hidden border p-2 ${
        isAlpha
          ? "rounded-v7-panel border-v7-ink/15 bg-v7-white text-v7-ink"
          : `${isDarkMode ? "bg-black" : "bg-white"} mb-2 mx-1 mt-1 rounded ${
              isDarkMode ? "border-gray-600" : "border-gray-300"
            } sm:mx-3 sm:mt-3`
      }`}
    >
      {showConnectPrompt && (
        <Link
          to="/positions/profile"
          className={`absolute inset-0 z-10 flex cursor-pointer items-center justify-center backdrop-blur-sm ${isAlpha ? "rounded-v7-panel bg-v7-ink/80" : "rounded bg-black/70"}`}
        >
          <span className={`px-4 text-center text-xs font-medium sm:text-sm ${isAlpha ? "text-v7-white" : "text-rp-amber"}`}>
            Click here to connect your IBKR account to see account information
          </span>
        </Link>
      )}
      {/* Primary Summary Row - Always Visible */}
      <div
        className={`cursor-pointer ${
          isAlpha ? "hover:bg-v7-paper" : isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-50"
        } -m-2 p-2 transition-colors`}
        onClick={onToggleExpand}
      >
        {/* Desktop Layout */}
        <div className="hidden sm:grid sm:grid-cols-6 gap-1 text-xs">
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Account
            </div>
            <div className={`${valueClass} font-mono text-xs leading-tight`}>
              {accountSummary?.accountCode || "N/A"}
            </div>
          </div>
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Net Liquidation
            </div>
            <div className="font-mono text-xs leading-tight">
              <span className={positiveValueClass}>
                {accountSummary ? formatUsdValue(accountSummary.netLiquidationSecurity) : "N/A"}
              </span>
            </div>
          </div>
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Cash Balance
            </div>
            <div className={`${valueClass} font-mono text-xs leading-tight`}>
              {accountSummary ? formatUsdValue(accountSummary.totalCashValueSecurity) : "N/A"}
            </div>
          </div>
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Gross Position
            </div>
            <div className={`${positiveValueClass} font-mono text-xs leading-tight`}>
              {accountSummary ? formatUsdValue(accountSummary.grossPositionValueSecurity) : "N/A"}
            </div>
          </div>
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Buying Power
            </div>
            <div className={`${positiveValueClass} font-mono text-xs leading-tight`}>
              {accountSummary ? formatUsdValue(accountSummary.buyingPower) : "N/A"}
            </div>
          </div>
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Positions
            </div>
            <div className="flex items-center justify-center gap-1">
              <span className={`${valueClass} font-mono text-xs leading-tight`}>
                {positionCount}
              </span>
              <ChevronDown
                className={`h-4 w-4 ${chevronClass} transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              />
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="grid grid-cols-2 gap-2 text-xs sm:hidden">
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Net Liq
            </div>
            <div className={`${positiveValueClass} font-mono text-xs`}>
              {accountSummary ? formatUsdValue(accountSummary.netLiquidationSecurity) : "N/A"}
            </div>
          </div>
          <div className="text-center">
            <div className={`${labelClass} text-xs leading-tight`}>
              Positions
            </div>
            <div className="flex items-center justify-center gap-1">
              <span className={`${valueClass} font-mono text-xs`}>
                {positionCount}
              </span>
              <ChevronDown
                className={`h-4 w-4 ${chevronClass} transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details Section -- hide when connect-prompt overlay is active */}
      {isExpanded && !showConnectPrompt && (
        <div className={`mt-2 border-t pt-2 ${isAlpha ? "border-v7-ink/15" : isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-1 text-xs mb-2">
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Available Funds
              </div>
              <div className={`${valueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? formatUsdValue(accountSummary.availableFundsSecurity) : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Excess Liquidity
              </div>
              <div className={`${valueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? formatUsdValue(accountSummary.excessLiquiditySecurity) : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Equity w/ Loan
              </div>
              <div className={`${valueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? formatUsdValue(accountSummary.equityWithLoanValueSecurity) : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Leverage
              </div>
              <div className={`${valueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? getPayloadValue("leverage-s") || "N/A" : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Cushion
              </div>
              <div className={`${cautionValueClass} font-mono text-xs leading-tight`}>
                {accountSummary
                  ? `${(parseFloat(getPayloadValue("cushion") || "0") * 100).toFixed(1)}%`
                  : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Account Type
              </div>
              <div className={`${valueClass} font-mono text-xs leading-tight`}>
                {accountSummary?.tradingTypeSecurity || "N/A"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-1 text-xs">
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Init Margin Req
              </div>
              <div className={`${infoValueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? formatUsdValue(accountSummary.initMarginReqSecurity) : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Maint Margin Req
              </div>
              <div className={`${infoValueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? formatUsdValue(accountSummary.maintenanceMarginReqSecurity) : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Accrued Dividend
              </div>
              <div className={`${valueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? formatUsdValue(accountSummary.accruedDividendSecurity) : "N/A"}
              </div>
            </div>
            <div className="text-center">
              <div className={`${labelClass} text-xs leading-tight`}>
                Accrued Cash
              </div>
              <div className={`${valueClass} font-mono text-xs leading-tight`}>
                {accountSummary ? formatUsdValue(accountSummary.accruedCashSecurity) : "N/A"}
              </div>
            </div>
            <div className="text-center"></div>
            <div className="text-center"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSummaryBar;
